import mixpanel from 'mixpanel-browser';
import { devLog } from '../utils';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;

/**
 * Mixpanel service
 */
class Mixpanel {
  constructor() {
    this.userType = null;
  }

  static setUserType(userType) {
    this.userType = userType;
  }

  static initialize() {
    if (MIXPANEL_TOKEN) {
      mixpanel.init(MIXPANEL_TOKEN, {
        persistence: 'localStorage',
        debug: process.env.NODE_ENV === 'development', // TODO find out why this isn't import.meta
        api_host: 'https://api-eu.mixpanel.com',
        ignore_dnt: true, // All tracked data is anonymous and used for our internal metrics
      });

      devLog('info', 'mixpanel', 'Mixpanel initialized');
    } else {
      devLog('warn', 'mixpanel', 'Mixpanel token not found');
    }
  }

  /**
   * Track an event
   * @param {string} eventName - Name of the event to track e.g. "Offer Creation Flow Started"
   * @param {Object} properties - Additional properties for the event
   *
   * TODO probably want event IDs to better uniquely identify them
   */
  static track(eventName, properties = {}) {
    if (import.meta.env.VITE_ENABLE_MIXPANEL !== 'true') {
      devLog(
        'warn',
        'mixpanel',
        `Event "${eventName}" not tracked: VITE_ENABLE_MIXPANEL !== 'true'`,
      );
      return;
    }

    if (!MIXPANEL_TOKEN) {
      devLog('warn', 'mixpanel', `Event "${eventName}" not tracked: Mixpanel not initialized`);
      return;
    }

    if (!this.userType || this.userType === 'staff') {
      devLog(
        'warn',
        'mixpanel',
        `Event "${eventName}" not tracked: Mixpanel is disabled for user type "${this.userType}"`,
      );
      return;
    }

    try {
      // Include the env so we can filter out dev/staging events
      const propertiesToTrack = {
        ...properties,
        env: import.meta.env.MODE,
        locale: import.meta.env.VITE_LOCALE ?? 'au',
      };
      mixpanel.track(eventName, propertiesToTrack);
      devLog('tracking', 'mixpanel', `Event tracked: ${eventName}`, properties);
    } catch (error) {
      devLog('error', 'mixpanel', `Failed to track event "${eventName}":`, error);
    }
  }

  /**
   * Sets the restaurant for better mixpanel tracking (tie events to a venue, apply super properties to all events)
   * @param {Object} rest - Restaurant object
   */
  static setRestaurant(rest) {
    if (import.meta.env.VITE_ENABLE_MIXPANEL !== 'true') {
      devLog('warn', 'mixpanel', `Restaurant not set: VITE_ENABLE_MIXPANEL !== 'true'`);
      return;
    }

    if (!MIXPANEL_TOKEN) {
      devLog('warn', 'mixpanel', `Restaurant not set: Mixpanel not initialized`);
      return;
    }

    if (!this.userType || this.userType === 'staff') {
      devLog(
        'warn',
        'mixpanel',
        `Restaurant not set: Mixpanel is disabled for user type "${this.userType}"`,
      );
      return;
    }

    try {
      /**
       * create/update the venue in mixpanel and tie future events to this venue
       * https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#identify
       */
      mixpanel.identify(rest.objectId);
      mixpanel.people.set({
        name: rest.name,
        region: rest.region,
      });

      /**
       * set some properties to apply to all future events
       * https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#setting-super-properties
       */
      mixpanel.register({
        rest_id: rest.objectId,
        rest_name: rest.name,
        env: import.meta.env.MODE,
        user_type: this.userType,
        locale: import.meta.env.VITE_LOCALE ?? 'au',
      });
      devLog('info', 'mixpanel', `Restaurant set: ${rest.name}`);
    } catch (error) {
      devLog('error', 'mixpanel', `Failed to set restaurant:`, error);
    }
  }

  /**
   * reset the identity when logging out or switching venues (venue groups) to handle multiple venues on one device
   * @see https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#call-reset-at-logout
   * @see https://docs.mixpanel.com/docs/tracking-methods/id-management/identifying-users-simplified#multiple-users-one-device
   */
  static reset() {
    if (import.meta.env.VITE_ENABLE_MIXPANEL !== 'true') {
      devLog('warn', 'mixpanel', "Identity not reset: VITE_ENABLE_MIXPANEL !== 'true'");
      return;
    }

    if (!MIXPANEL_TOKEN) {
      devLog('warn', 'mixpanel', 'Identity not reset: Mixpanel not initialized');
      return;
    }

    if (!this.userType || this.userType === 'staff') {
      devLog(
        'warn',
        'mixpanel',
        `Identity not reset: Mixpanel is disabled for user type "${this.userType}"`,
      );
      return;
    }

    try {
      mixpanel.reset();
      devLog('tracking', 'mixpanel', 'Identity reset');
    } catch (error) {
      devLog('error', 'mixpanel', 'Failed to reset identity:', error);
    }
  }
}

export default Mixpanel;
